import React, { useEffect, useRef, useState } from 'react';
import {
  Heart, Brain, Utensils, Dumbbell, Sparkles, LogIn, BarChart3,
  ChevronDown, ArrowRight, Compass, TrendingUp, Eye, Activity, Footprints, Users, MessageCircle, Check,
  Camera, Type, Film, Play,
} from 'lucide-react';
import nikkiImg from '../../assets/nikki/nikki.png';

/**
 * PreAuthHero — story-driven landing for unauthenticated visitors.
 *
 * Flow (vision-first, Nikki is the guide — not the headline):
 * 1 Hero (Understand yourself + avatar/4-worlds visual) → 2 How it works
 * (Awareness→Action→Growth) → 3 The Living World (worlds map + evolving world)
 * → 4 Choose your mode (Insights vs Journey, interactive) → 5 Meet Nikki →
 * 6 Community (the Village) → 7 Final CTA. Reuses the 16 shipped scenery PNGs +
 * a generated hero image. CSS reveal-on-scroll, no extra deps.
 */

const WORLD_SRC = (pillar, state) => `/assets/living/worlds/${pillar}_${state}.webp`;
const AVATAR_SRC = (pillar, state) => `/assets/landing/avatars/${pillar}_${state}.webp`;
const HERO_SRC = '/assets/living/hero/hero-worlds.png?v=5';
const STATES = ['poor', 'fair', 'good', 'excellent'];
const STATE_LABELS = ['Dormant', 'Stirring', 'Flourishing', 'Thriving'];

const PILLARS = [
  { key: 'movement',  label: 'Movement', icon: Dumbbell, color: '#CCFF00' },
  { key: 'recovery',  label: 'Recovery', icon: Heart,    color: '#22d3ee' },
  { key: 'nutrition', label: 'Nutrition', icon: Utensils, color: '#fb923c' },
  { key: 'mental',    label: 'Mind',     icon: Brain,    color: '#a855f7' },
];

// Sample "thriving" snapshot for the hero — shows wellness made visible.
const HERO_PILLARS = [
  { key: 'movement',  label: 'Movement',  icon: Dumbbell, color: '#CCFF00', score: 91, delta: 4, state: 'Energized', pos: 'top-0 left-0' },
  { key: 'recovery',  label: 'Recovery',  icon: Heart,    color: '#5BDCF5', score: 84, delta: 3, state: 'Rested',    pos: 'top-0 right-0' },
  { key: 'nutrition', label: 'Nutrition', icon: Utensils, color: '#fb923c', score: 87, delta: 5, state: 'Nourished', pos: 'bottom-0 left-0' },
  { key: 'mental',    label: 'Mind',      icon: Brain,    color: '#C08CFF', score: 90, delta: 2, state: 'Clear',     pos: 'bottom-0 right-0' },
];

const STEPS = [
  { icon: Eye,        title: 'Awareness', desc: 'See what your movement, recovery, nutrition and mind are really doing — and why each one matters.', color: '#CCFF00' },
  { icon: Footprints, title: 'Action',    desc: 'Nikki turns it into one clear next step — and keeps you accountable, day after day.', color: '#22d3ee' },
  { icon: TrendingUp, title: 'Growth',    desc: 'Your Wellness IQ climbs and your worlds come alive as the changes stick.', color: '#a855f7' },
];

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add('reveal-in'); io.unobserve(el); } },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
};

const Reveal = ({ children, delay = 0, className = '', testId }) => {
  const ref = useReveal();
  return (
    <div ref={ref} className={`reveal-init ${className}`} style={{ transitionDelay: `${delay}ms` }} data-testid={testId}>
      {children}
    </div>
  );
};

const SectionHead = ({ kicker, title, sub, kickerColor = '#CCFF00' }) => (
  <Reveal className="max-w-2xl mx-auto text-center mb-9">
    {kicker && <p className="text-[11px] uppercase tracking-[0.25em] font-secondary font-bold mb-2" style={{ color: kickerColor }}>{kicker}</p>}
    <h2 className="text-3xl md:text-4xl font-secondary font-bold tracking-tight text-white">{title}</h2>
    {sub && <p className="text-base text-zinc-400 mt-3">{sub}</p>}
  </Reveal>
);

const WorldEvolution = () => {
  const [pillar, setPillar] = useState('mental');
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % STATES.length), 2100);
    return () => clearInterval(t);
  }, []);
  const meta = PILLARS.find((p) => p.key === pillar);
  return (
    <div data-testid="world-evolution">
      <div className="relative w-full aspect-video rounded-3xl overflow-hidden border" style={{ borderColor: `${meta.color}40`, boxShadow: `0 0 60px ${meta.color}22` }}>
        {STATES.map((s, i) => (
          <img key={s} src={AVATAR_SRC(pillar, s)} alt={`${meta.label} ${s}`}
            className={`absolute inset-0 w-full h-full object-cover ${i === idx ? 'animate-world-zoom' : ''}`}
            style={{ opacity: i === idx ? 1 : 0, transition: 'opacity 1.1s ease-in-out' }} loading="lazy" decoding="async" />
        ))}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 45%)' }} />
        <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] font-secondary font-bold" style={{ color: meta.color }}>{meta.label} World</p>
            <p className="font-secondary font-bold text-3xl text-white leading-none mt-1" data-testid="world-evo-state">{STATE_LABELS[idx]}</p>
          </div>
          <div className="flex gap-1.5">
            {STATES.map((s, i) => (
              <span key={s} className="h-1.5 rounded-full transition-all duration-500" style={{ width: i === idx ? 22 : 8, background: i === idx ? meta.color : 'rgba(255,255,255,0.25)' }} />
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
        {PILLARS.map((p) => {
          const Icon = p.icon; const on = p.key === pillar;
          return (
            <button key={p.key} onClick={() => { setPillar(p.key); setIdx(0); }} data-testid={`world-evo-pillar-${p.key}`}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-secondary font-bold border transition-all active:scale-95"
              style={{ borderColor: on ? p.color : 'rgba(255,255,255,0.12)', color: on ? p.color : '#a1a1aa', background: on ? `${p.color}1a` : 'transparent' }}>
              <Icon className="w-3.5 h-3.5" /> {p.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const WorldsMap = () => (
  <div className="relative mx-auto max-w-2xl" data-testid="worlds-map">
    <div className="grid grid-cols-2 gap-4 sm:gap-6">
      {PILLARS.map((p) => (
        <div key={p.key} className="relative aspect-[4/3] rounded-2xl overflow-hidden border group" style={{ borderColor: `${p.color}33` }}>
          <img src={WORLD_SRC(p.key, 'excellent')} alt={`${p.label} world`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${p.color}55, transparent 60%)` }} />
          <div className="absolute bottom-0 left-0 p-3 flex items-center gap-1.5">
            <p.icon className="w-4 h-4 text-white" />
            <span className="font-secondary font-bold text-sm text-white tracking-tight">{p.label} World</span>
          </div>
        </div>
      ))}
    </div>
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none">
      <div className="relative animate-orb-float">
        <div className="absolute -inset-6 rounded-full bg-primary/20 blur-2xl" />
        <div className="absolute -inset-2 rounded-full border border-primary/30 animate-spin-slow" />
        <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full grid place-items-center bg-[#0A0A0A] border-2 border-primary shadow-[0_0_40px_rgba(204,255,0,0.45)]">
          <div className="text-center">
            <Sparkles className="w-6 h-6 text-primary mx-auto" />
            <p className="text-[9px] uppercase tracking-[0.25em] text-primary font-secondary font-bold mt-0.5">You</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const StepVisual = ({ step }) => {
  if (step.title === 'Awareness') {
    return (
      <div className="grid grid-cols-2 gap-2 mt-4" data-testid="step-visual-awareness">
        {PILLARS.map((p) => (
          <div key={p.key} className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg border" style={{ borderColor: `${p.color}40`, background: `${p.color}12` }}>
            <p.icon className="w-3.5 h-3.5 shrink-0" style={{ color: p.color }} />
            <span className="text-[11px] font-bold text-white/80 truncate">{p.label}</span>
          </div>
        ))}
      </div>
    );
  }
  if (step.title === 'Action') {
    const tasks = ['Complete a workout', 'Do a mind session', 'Log a healthy meal', 'Do your daily check-in'];
    return (
      <div className="mt-4 space-y-1.5" data-testid="step-visual-action">
        {tasks.map((t) => (
          <div key={t} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-black/30 border border-white/10">
            <span className="w-4 h-4 rounded-full grid place-items-center shrink-0" style={{ background: step.color }}><Check className="w-2.5 h-2.5 text-black" strokeWidth={3} /></span>
            <span className="text-[11px] text-white/80">{t}</span>
          </div>
        ))}
      </div>
    );
  }
  // Growth — a rising mini chart climbing to 88
  const bars = [38, 50, 47, 62, 74, 88];
  return (
    <div className="mt-4" data-testid="step-visual-growth">
      <div className="flex items-end gap-1.5 h-14">
        {bars.map((h, idx) => (
          <div key={idx} className="flex-1 rounded-t-md" style={{ height: `${h}%`, background: idx === bars.length - 1 ? step.color : `${step.color}44` }} />
        ))}
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-sm font-secondary font-bold text-white/40 tabular-nums">52</span>
        <ArrowRight className="w-4 h-4" style={{ color: step.color }} />
        <span className="font-secondary font-black text-2xl leading-none tabular-nums" style={{ color: step.color }}>88</span>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// What you can actually do — one row per pillar, with a code-built mini-visual
// that mirrors the real in-app feature (form camera, muscle map, photo meal
// logging, mood check-in). Alternating layout, reuses Reveal animations.
// ──────────────────────────────────────────────────────────────────────────

// Shared, image-free panel so all four visuals carry one consistent look/flow:
// a dark surface, a faint motion grid, and a soft pillar-tinted glow.
const VizShell = ({ color, children, testId }) => (
  <div className="relative w-full aspect-[4/3] rounded-2xl border overflow-hidden bg-[#0b0b0f]" style={{ borderColor: `${color}33` }} data-testid={testId}>
    <div className="absolute inset-0" style={{ background: `radial-gradient(120% 85% at 50% 0%, ${color}22, transparent 62%)` }} />
    <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)', backgroundSize: '22px 22px' }} />
    <div className="relative w-full h-full">{children}</div>
  </div>
);

// Movement — your real recorded clip (phone-framed, autoplaying inline) beside
// the post-workout biomechanics report. Compact — no pop-out.
const MovementVisual = ({ color }) => (
  <VizShell color={color} testId="feat-visual-movement">
    <div className="absolute inset-0 flex items-stretch gap-3 p-4">
      <div className="relative h-full aspect-[1/2] shrink-0 rounded-xl overflow-hidden border-2 bg-black" style={{ borderColor: `${color}55` }}>
        <video
          poster="/assets/landing/movement-final-poster.jpg"
          autoPlay muted loop playsInline preload="metadata"
          className="w-full h-full object-cover"
        >
          <source src="/assets/landing/movement-final.webm" type="video/webm" />
          <source src="/assets/landing/movement-final.mp4" type="video/mp4" />
        </video>
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 border border-white/15 text-[8px] font-bold uppercase tracking-widest text-white/85">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Live
        </span>
        <span className="absolute bottom-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/70 border border-white/15 text-[8.5px] font-bold uppercase tracking-widest text-white/90 whitespace-nowrap">
          <Play className="w-2.5 h-2.5" fill="currentColor" style={{ color }} /> See it in action
        </span>
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-center gap-2.5">
        <span className="inline-flex items-center gap-1.5 self-start px-2 py-1 rounded-full bg-black/50 border border-white/10">
          <Film className="w-3 h-3" style={{ color }} />
          <span className="text-[9px] font-bold uppercase tracking-widest text-white/75">Post-workout analysis</span>
        </span>
        <div className="rounded-xl bg-black/45 border border-white/10 p-2.5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-white">Back Squat</span>
            <span className="font-secondary font-bold text-xl leading-none tabular-nums" style={{ color }}>86</span>
          </div>
          <div className="space-y-1.5">
            {[['Knee angle', '92°'], ['Hip depth', '88°'], ['Tempo', '✓']].map(([l, v]) => (
              <div key={l} className="flex items-center justify-between text-[10px]">
                <span className="text-zinc-400">{l}</span>
                <span className="font-bold text-white tabular-nums">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </VizShell>
);

// Recovery — the app's real Muscle Recovery Map screenshot (portrait), shown
// full + centered so nothing is cropped and there's no empty filler space.
const RecoveryVisual = ({ color }) => (
  <div className="flex justify-center" data-testid="feat-visual-recovery">
    <div className="relative w-full max-w-[240px] rounded-2xl border overflow-hidden bg-[#0a121a]" style={{ borderColor: `${color}40`, boxShadow: `0 0 44px ${color}1f` }}>
      <img
        src="/assets/landing/recovery-map.png"
        alt="Muscle recovery map showing front and back body with recovery levels"
        className="block w-full h-auto"
        loading="lazy"
      />
    </div>
  </div>
);

// Nutrition — a real meal photo + Nikki's auto-filled macros (snap or type it).
const NutritionVisual = ({ color }) => (
  <VizShell color={color} testId="feat-visual-nutrition">
    <img src="/assets/landing/meal.jpg" alt="A healthy meal logged by Nikki" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
    <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.92) 6%, rgba(0,0,0,0.05) 55%)' }} />
    <div className="absolute top-3 left-3 flex gap-1.5">
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-black/60 border border-white/15 text-[9px] font-bold text-white/85"><Camera className="w-3 h-3" style={{ color }} /> Photo</span>
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-black/60 border border-white/15 text-[9px] font-bold text-white/85"><Type className="w-3 h-3" style={{ color }} /> or type</span>
    </div>
    <div className="absolute bottom-3 left-3 right-3">
      <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color }}>Logged by Nikki ✓</p>
      <div className="flex gap-1.5">
        {[['Protein', '32g'], ['Carbs', '45g'], ['Fat', '12g'], ['kcal', '420']].map(([l, v]) => (
          <div key={l} className="flex-1 rounded-lg bg-black/55 border border-white/10 px-1.5 py-1 text-center">
            <p className="font-secondary font-bold text-sm text-white leading-none tabular-nums">{v}</p>
            <p className="text-[7.5px] uppercase tracking-widest text-zinc-300 mt-0.5">{l}</p>
          </div>
        ))}
      </div>
    </div>
  </VizShell>
);

// Mind — the app's daily check-in: Mood / Energy / Stress self-report sliders.
const MindVisual = ({ color }) => {
  const rows = [
    { label: 'Mood', val: 4, tag: 'Good' },
    { label: 'Energy', val: 4, tag: 'High' },
    { label: 'Stress', val: 2, tag: 'Low' },
  ];
  return (
    <VizShell color={color} testId="feat-visual-mental">
      <div className="absolute inset-0 p-5 flex flex-col justify-center gap-4">
        <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color }}>Daily check-in</p>
        {rows.map((r) => (
          <div key={r.label}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-bold text-white/85">{r.label}</span>
              <span className="text-[10px] font-bold text-zinc-400 tabular-nums">{r.tag} · {r.val}/5</span>
            </div>
            <div className="relative h-2 rounded-full bg-white/10">
              <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${(r.val / 5) * 100}%`, background: color }} />
              <span className="absolute top-1/2 w-4 h-4 rounded-full border-2 bg-[#0b0b0f]" style={{ left: `${(r.val / 5) * 100}%`, transform: 'translate(-50%, -50%)', borderColor: color }} />
            </div>
          </div>
        ))}
      </div>
    </VizShell>
  );
};

const PILLAR_FEATURES = [
  { key: 'movement', label: 'Movement', color: '#CCFF00', icon: Activity, tag: 'Live view & analysis',
    title: 'See your form, frame by frame.', Visual: MovementVisual,
    desc: 'Train in front of the camera to watch yourself live, or record a clip — then after your workout Nikki breaks down your technique with joint angles, a form score and injury-risk flags. A camera-tracked mobility check maps your range so every plan fits how you actually move.' },
  { key: 'recovery', label: 'Recovery', color: '#22d3ee', icon: Heart, tag: 'Muscle recovery map',
    title: 'See exactly what\u2019s recovered.', Visual: RecoveryVisual,
    desc: 'A living muscle map shows which muscles are fresh and which still need rest — so you train what\u2019s ready and let the rest heal, instead of guessing.' },
  { key: 'nutrition', label: 'Nutrition', color: '#fb923c', icon: Utensils, tag: 'Effortless meal logging',
    title: 'Snap it or just say it.', Visual: NutritionVisual,
    desc: 'Take a photo of your plate or simply type what you ate — Nikki recognises it and logs the nutrition for you. No barcodes, no weighing, no tedious calorie counting.' },
  { key: 'mental', label: 'Mind', color: '#a855f7', icon: Brain, tag: 'Check-ins & breathwork',
    title: 'A 30-second check-in.', Visual: MindVisual,
    desc: 'Quick mood and stress check-ins capture how you really feel, and guided breathwork helps you reset — keeping your head as cared-for as your body.' },
];

const PillarFeatures = () => (
  <section className="px-5 py-14" data-testid="pillar-features">
    <SectionHead kicker="What you can actually do" kickerColor="#22d3ee"
      title="Four pillars, four superpowers"
      sub="Each world has its own effortless way in. No spreadsheets, no manual entry — just point, snap, tap or breathe." />
    <div className="max-w-4xl mx-auto space-y-6 sm:space-y-10">
      {PILLAR_FEATURES.map((f, i) => {
        const Icon = f.icon; const Visual = f.Visual; const flip = i % 2 === 1;
        return (
          <Reveal key={f.key} delay={i * 80} testId={`pillar-feature-${f.key}`}>
            <div className={`grid sm:grid-cols-2 gap-5 sm:gap-9 items-center ${!flip ? 'sm:[&>*:first-child]:order-2' : ''}`}>
              <div>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-secondary font-bold uppercase tracking-widest mb-3"
                  style={{ color: f.color, background: `${f.color}1a`, border: `1px solid ${f.color}40` }}>
                  <Icon className="w-3.5 h-3.5" /> {f.label} · {f.tag}
                </span>
                <h3 className="font-secondary font-bold text-2xl sm:text-3xl tracking-tight text-white leading-tight">{f.title}</h3>
                <p className="text-sm sm:text-base text-zinc-400 leading-relaxed mt-3">{f.desc}</p>
              </div>
              <Visual color={f.color} />
            </div>
          </Reveal>
        );
      })}
    </div>
  </section>
);

// Avatar arena — you at the centre of your four wellness worlds (moved here from
// the top hero so the top stays clean and the flow lands in the magic section).
const AvatarArena = () => (
  <div className="relative mx-auto w-full max-w-[330px] sm:max-w-[380px]" data-testid="avatar-arena">
    <div className="flex items-center justify-center gap-2.5 mb-4" data-testid="hero-iq">
      <span className="text-[10px] uppercase tracking-[0.3em] font-secondary font-bold text-primary">Wellness IQ</span>
      <span className="font-secondary font-black text-3xl text-primary leading-none tabular-nums">88</span>
      <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-primary/70">Thriving</span>
    </div>
    <div className="relative aspect-[6/5]">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full bg-primary/20 blur-3xl animate-pulse-slow" />
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
        <line x1="50" y1="50" x2="15" y2="18" stroke="#CCFF00" strokeWidth="0.5" strokeOpacity="0.22" />
        <line x1="50" y1="50" x2="85" y2="18" stroke="#5BDCF5" strokeWidth="0.5" strokeOpacity="0.22" />
        <line x1="50" y1="50" x2="15" y2="82" stroke="#fb923c" strokeWidth="0.5" strokeOpacity="0.22" />
        <line x1="50" y1="50" x2="85" y2="82" stroke="#C08CFF" strokeWidth="0.5" strokeOpacity="0.22" />
      </svg>
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
        <div className="relative w-[112px] h-[140px] sm:w-[120px] sm:h-[150px] rounded-2xl overflow-hidden border-2 border-primary/50 shadow-[0_0_55px_rgba(204,255,0,0.4)] animate-orb-float">
          <img src={HERO_SRC} alt="Your wellness avatar" data-testid="hero-visual" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.45), transparent 55%)' }} />
        </div>
      </div>
      {HERO_PILLARS.map((p, i) => {
        const Icon = p.icon;
        return (
          <div key={p.key} data-testid={`hero-pillar-${p.key}`}
            className={`absolute ${p.pos} w-[104px] sm:w-[114px] p-2.5 rounded-[18px] flex flex-col gap-1 bg-black/75 backdrop-blur-xl border shadow-[0_8px_28px_rgba(0,0,0,0.5)] z-20 animate-orb-float`}
            style={{ borderColor: `${p.color}55`, animationDelay: `${0.4 + i * 0.5}s` }}>
            <span className="flex items-center gap-1.5">
              <Icon className="w-3.5 h-3.5" style={{ color: p.color }} />
              <span className="text-[10px] font-bold uppercase tracking-wider text-white/70">{p.label}</span>
            </span>
            <span className="flex items-baseline gap-1.5">
              <span className="font-secondary font-bold text-2xl leading-none text-white tabular-nums">{p.score}</span>
              <span className="text-[10px] font-bold flex items-center" style={{ color: '#4ade80' }}>
                <TrendingUp className="w-3 h-3" />{p.delta}
              </span>
            </span>
            <span className="text-[9.5px] font-bold uppercase tracking-widest leading-none" style={{ color: p.color }}>{p.state}</span>
          </div>
        );
      })}
    </div>
  </div>
);

const PreAuthHero = ({ onChoice }) => {
  const startNew = () => onChoice?.('new');
  const startExisting = () => onChoice?.('existing');

  return (
    <div className="relative flex-1 overflow-x-hidden" data-testid="preauth-hero">
      <div className="absolute inset-0 pointer-events-none -z-10">
        <div className="absolute inset-0 bg-gradient-radial-pulse" />
        <div className="absolute inset-0 bg-grid-faint" />
        <div className="absolute top-0 -left-24 w-72 h-72 bg-primary/15 rounded-full blur-[120px] animate-pulse-slow" />
        <div className="absolute top-1/2 -right-24 w-80 h-80 bg-accent-blue/10 rounded-full blur-[140px] animate-pulse-slower" />
      </div>

      {/* ───── 1 · HERO (vision-first, no image — flows into the magic section) ───── */}
      <section className="min-h-[70vh] flex items-center px-5 py-16">
        <div className="max-w-3xl mx-auto flex flex-col items-center w-full">
          <div className="text-center">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-primary/30 bg-primary/5 mb-5">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
              <span className="text-[11px] uppercase tracking-[0.2em] text-primary font-secondary font-bold">Wellness Intelligence</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-secondary font-bold tracking-tight leading-[1.05]" data-testid="hero-headline">
              Awareness.<br />Action.<br />Growth.
            </h1>
            <p className="text-base md:text-lg text-zinc-300 leading-relaxed mt-5 max-w-md mx-auto" data-testid="hero-tagline">
              Not just your numbers — see how your daily choices shape your movement, recovery, nutrition and mental wellness, and turn that understanding into lasting change.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
              <button type="button" onClick={startNew} data-testid="hero-choice-new"
                className="group px-7 py-3 rounded-sm bg-primary text-black font-secondary font-bold tracking-wide text-sm flex items-center justify-center gap-2 hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(204,255,0,0.4)] transition-all">
                <Sparkles className="w-4 h-4" /> Start your journey
              </button>
              <button type="button" onClick={startExisting} data-testid="hero-choice-existing"
                className="px-7 py-3 rounded-sm border border-zinc-700 bg-zinc-900/60 hover:border-primary hover:text-primary text-sm font-secondary font-bold tracking-wide flex items-center justify-center gap-2 transition-all">
                <LogIn className="w-4 h-4" /> I have an account
              </button>
            </div>
            <div className="flex items-center gap-2 mt-6 justify-center" data-testid="hero-nikki-chip">
              <img src={nikkiImg} alt="Nikki" className="w-7 h-7 rounded-full border border-primary/40 object-cover" />
              <span className="text-xs text-zinc-500">Guided every step by <span className="text-zinc-300 font-semibold">Nikki</span>, your wellness guide</span>
            </div>
          </div>
        </div>
      </section>

      <button type="button" onClick={() => window.scrollBy({ top: window.innerHeight * 0.8, behavior: 'smooth' })}
        className="mx-auto -mt-4 mb-4 flex flex-col items-center gap-1 text-zinc-500 hover:text-primary transition-colors" data-testid="hero-scroll-cue">
        <span className="text-[10px] uppercase tracking-[0.25em] font-secondary font-bold">See how it works</span>
        <ChevronDown className="w-5 h-5 animate-scroll-cue" />
      </button>

      {/* ───── 2 · HOW IT WORKS ───── */}
      <section className="px-5 py-14">
        <SectionHead kicker="How it works" title="Three steps, every day"
          sub="Awareness becomes action — and action becomes lasting growth." />
        <div className="flex flex-col sm:flex-row items-stretch gap-3 max-w-4xl mx-auto">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.title}>
              <Reveal delay={i * 120} className="flex-1">
                <div className="relative h-full rounded-3xl border p-5 overflow-hidden" data-testid={`step-${s.title.toLowerCase()}`}
                  style={{ borderColor: `${s.color}44`, background: `linear-gradient(140deg, ${s.color}24, rgba(10,10,10,0.5) 70%)`, boxShadow: `0 0 36px ${s.color}1f` }}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-2xl grid place-items-center shrink-0" style={{ background: `${s.color}26`, border: `1px solid ${s.color}66`, boxShadow: `0 0 24px ${s.color}40` }}>
                      <s.icon className="w-6 h-6" style={{ color: s.color }} />
                    </div>
                    <h3 className="font-secondary font-bold text-2xl text-white">{s.title}</h3>
                  </div>
                  <p className="text-sm text-zinc-300 leading-relaxed">{s.desc}</p>
                  <StepVisual step={s} />
                </div>
              </Reveal>
              {i < STEPS.length - 1 && (
                <Reveal delay={400 + i * 220} className="flex items-center justify-center shrink-0" testId={`step-arrow-${i}`}>
                  <span style={{ color: STEPS[i + 1].color }}>
                    <ChevronDown className="w-6 h-6 sm:hidden opacity-80" />
                    <ArrowRight className="w-6 h-6 hidden sm:block opacity-80" />
                  </span>
                </Reveal>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ───── 2b · WHAT YOU CAN ACTUALLY DO ───── */}
      <PillarFeatures />

      {/* ───── 3 · THE LIVING WORLD ───── */}
      <section className="px-5 py-14 bg-gradient-to-b from-transparent via-black/40 to-transparent">
        <SectionHead kicker="The magic" title="Your wellness becomes a world"
          sub="You sit at the centre of four living worlds. Every workout, good night's sleep and healthy meal transforms them — from dim and dormant to alive and thriving." />
        <Reveal><AvatarArena /></Reveal>
        <Reveal delay={120} className="max-w-3xl mx-auto mt-12">
          <WorldEvolution />
        </Reveal>
      </section>

      {/* ───── 4 · CHOOSE YOUR MODE ───── */}
      <section className="px-5 py-14">
        <SectionHead kicker="One platform · two ways to see yourself"
          title="Two ways to experience it"
          sub="Some people understand wellness through numbers. Others through stories. You get both — switch any time inside the app." />
        <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
          <Reveal>
            <div data-testid="mode-card-insights"
              className="h-full rounded-3xl border border-primary/25 bg-zinc-900/40 backdrop-blur-xl p-6 flex flex-col" style={{ boxShadow: '0 0 40px rgba(204,255,0,0.05)' }}>
              <div className="w-12 h-12 rounded-2xl grid place-items-center bg-primary/15 border border-primary/30 mb-4">
                <BarChart3 className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-secondary font-bold text-2xl text-white">Insights Mode</h3>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">Numbers, scores and deep analysis. Movement, recovery, nutrition and mind — measured, tracked and explained. For when you love the data.</p>
              <div className="grid grid-cols-4 gap-2 mt-5">
                {PILLARS.map((p, i) => (
                  <div key={p.key} className="rounded-xl bg-black/40 border border-white/5 p-2 text-center">
                    <p className="font-secondary font-bold text-xl text-white leading-none">{[82, 74, 91, 68][i]}</p>
                    <p className="text-[8px] uppercase tracking-widest text-zinc-500 mt-1">{p.label}</p>
                  </div>
                ))}
              </div>
              <p className="mt-auto pt-5 text-[11px] uppercase tracking-widest text-primary font-secondary font-bold flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Clarity in numbers</p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div data-testid="mode-card-journey"
              className="relative h-full rounded-3xl border overflow-hidden p-6 flex flex-col" style={{ borderColor: 'rgba(168,85,247,0.35)', boxShadow: '0 0 40px rgba(168,85,247,0.08)' }}>
              <img src={WORLD_SRC('mental', 'excellent')} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" loading="lazy" />
              <div className="relative flex flex-col h-full">
                <div className="w-12 h-12 rounded-2xl grid place-items-center mb-4" style={{ background: 'rgba(168,85,247,0.18)', border: '1px solid rgba(168,85,247,0.4)' }}>
                  <Compass className="w-6 h-6" style={{ color: '#c084fc' }} />
                </div>
                <h3 className="font-secondary font-bold text-2xl text-white">Journey Mode</h3>
                <p className="text-sm text-zinc-300 mt-2 leading-relaxed">Your wellness becomes a living world. Your avatar and four worlds evolve with every healthy choice — transforming the life you're building.</p>
                <div className="flex -space-x-2 mt-5">
                  {PILLARS.map((p) => (
                    <span key={p.key} className="w-10 h-10 rounded-full border-2 border-[#0A0A0A] grid place-items-center" style={{ background: `${p.color}22` }}>
                      <p.icon className="w-4 h-4" style={{ color: p.color }} />
                    </span>
                  ))}
                </div>
                <p className="mt-auto pt-6 text-[11px] uppercase tracking-widest font-secondary font-bold flex items-center gap-1.5" style={{ color: '#c084fc' }}><Eye className="w-3.5 h-3.5" /> Wellness made visible</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───── 5 · MEET NIKKI ───── */}
      <section className="px-5 py-14 bg-gradient-to-b from-transparent via-black/40 to-transparent">
        <div className="max-w-4xl mx-auto grid sm:grid-cols-[auto_1fr] gap-8 items-center">
          <Reveal className="mx-auto">
            <div className="relative">
              <div className="absolute -inset-4 rounded-full bg-primary/10 blur-2xl animate-pulse-slow" />
              <div className="absolute inset-0 rounded-full border border-primary/20 animate-ping-slow" />
              <img src={nikkiImg} alt="Nikki" className="relative w-32 h-32 rounded-full border-2 border-primary/50 object-cover shadow-[0_0_50px_rgba(204,255,0,0.2)]" data-testid="meet-nikki-img" />
            </div>
          </Reveal>
          <Reveal delay={100} className="text-center sm:text-left">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-primary/30 bg-primary/5 mb-4" data-testid="nikki-badge">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
              <span className="text-[11px] uppercase tracking-[0.2em] text-primary font-secondary font-bold">Your Wellness Guide</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-secondary font-bold tracking-tight text-white">Hi, I&apos;m Nikki</h2>
            <p className="text-base text-zinc-300 leading-relaxed mt-3">
              Your guide to understanding yourself. Together we&apos;ll explore how your movement, recovery, nutrition and mental wellness connect — and help you turn that understanding into lasting change.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ───── 6 · COMMUNITY ───── */}
      <section className="px-5 py-14">
        <SectionHead kicker="Community" kickerColor="#22d3ee" title="Grow your world together"
          sub="Join a shared Village for each world — chase community goals, leave anonymous words of wisdom, and watch the village flourish as everyone shows up." />
        <Reveal className="max-w-4xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden border border-accent-blue/25" style={{ boxShadow: '0 0 50px rgba(34,211,238,0.1)' }}>
            <img src={WORLD_SRC('nutrition', 'excellent')} alt="A flourishing community village" className="w-full h-56 sm:h-72 object-cover" loading="lazy" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent 60%)' }} />
            <div className="absolute bottom-0 left-0 right-0 p-6 flex flex-wrap gap-5">
              <span className="flex items-center gap-2 text-sm text-white font-secondary font-bold"><Users className="w-4 h-4 text-accent-blue" /> Shared village goals</span>
              <span className="flex items-center gap-2 text-sm text-white font-secondary font-bold"><MessageCircle className="w-4 h-4 text-accent-blue" /> Anonymous wisdom</span>
              <span className="flex items-center gap-2 text-sm text-white font-secondary font-bold"><Sparkles className="w-4 h-4 text-accent-blue" /> Evolving scenery</span>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ───── 7 · FINAL CTA ───── */}
      <section className="px-5 pt-10 pb-28 text-center">
        <Reveal className="max-w-2xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-secondary font-bold tracking-tight text-white leading-tight">Make your wellness <span className="text-primary">visible</span>.</h2>
          <p className="text-base md:text-lg text-zinc-400 mt-4">Understand your body through data, or live it as a world that grows with every choice. Nikki guides you either way.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-9">
            <button type="button" onClick={startNew} data-testid="hero-cta-start-bottom"
              className="group px-8 py-3.5 rounded-sm bg-primary text-black font-secondary font-bold tracking-wide text-sm flex items-center justify-center gap-2 hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(204,255,0,0.4)] transition-all">
              <Sparkles className="w-4 h-4" /> Start your journey <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
            <button type="button" onClick={startExisting} data-testid="hero-cta-existing-bottom"
              className="px-8 py-3.5 rounded-sm border border-zinc-700 bg-zinc-900/60 hover:border-primary hover:text-primary text-sm font-secondary font-bold tracking-wide flex items-center justify-center gap-2 transition-all">
              <LogIn className="w-4 h-4" /> I have an account
            </button>
          </div>
          <p className="text-[10px] text-zinc-600 mt-10 flex items-center justify-center gap-1.5" data-testid="hero-footer">
            <Activity className="w-3 h-3" /> Wellness Intelligence · Revivo IQ
          </p>
        </Reveal>
      </section>
    </div>
  );
};

export default PreAuthHero;
